import { useState, useEffect } from 'react';
import { Phone, PhoneOff, Mic, MicOff, X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useVoice } from '@/context/VoiceContext';
import { toast } from 'sonner';
import { normalizePhone, formatPhoneDisplay } from '@/lib/phone';

function formatDuration(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function Dialer() {
  const navigate = useNavigate();
  const {
    makeCall, hangUp, toggleMute, callStatus, isMuted,
    callDuration, callerInfo, isDeviceReady, device,
    dialRequest, clearDialRequest,
  } = useVoice();

  const [open, setOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [calling, setCalling] = useState(false);

  // Auto-open mini panel when a call starts
  useEffect(() => {
    if (callStatus !== 'idle') setOpen(true);
  }, [callStatus]);

  // Click-to-call from anywhere: prefill the number and dial it right away
  useEffect(() => {
    if (!dialRequest) return;
    const number = normalizePhone(dialRequest.number);
    setPhoneInput(formatPhoneDisplay(number));
    setOpen(true);
    clearDialRequest();

    if (!dialRequest.autoCall) return;
    if (callStatus !== 'idle') {
      toast.warning('Already on a call — number loaded in Quick Dial.');
      return;
    }
    if (!device) {
      toast.error('Voice service not available. Number loaded — configure Twilio to call.');
      return;
    }
    setCalling(true);
    makeCall(number, dialRequest.name || number)
      .then(() => setPhoneInput(''))
      .catch(err => toast.error(`Call failed: ${err.message}`))
      .finally(() => setCalling(false));
  }, [dialRequest]);

  const handleCall = async () => {
    if (!phoneInput.trim()) return;
    if (!device) {
      toast.error('Voice service not available. Configure Twilio credentials.');
      return;
    }
    setCalling(true);
    try {
      await makeCall(normalizePhone(phoneInput.trim()), phoneInput.trim());
      setPhoneInput('');
    } catch (err) {
      toast.error(`Call failed: ${err.message}`);
    } finally {
      setCalling(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && callStatus === 'idle') handleCall();
  };

  const isOnCall = callStatus !== 'idle';

  return (
    <>
      {/* Floating trigger button */}
      <button
        data-testid="dialer-fab"
        onClick={() => setOpen(o => !o)}
        className={`
          fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl 
          flex items-center justify-center transition-all duration-200
          ${isOnCall
            ? 'bg-emerald-600 hover:bg-emerald-700 animate-pulse'
            : 'bg-[#003366] hover:bg-[#004488]'
          }
        `}
      >
        <Phone className="w-6 h-6 text-white" strokeWidth={2} />
        {isOnCall && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white" />
        )}
      </button>

      {/* Mini dialer / active-call panel */}
      {open && (
        <div
          data-testid="dialer-panel"
          className="fixed bottom-24 right-6 z-50 w-72 bg-[#0F172A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#3399FF]" />
              <span className="text-sm font-semibold text-white">
                {isOnCall ? 'On Call' : 'Quick Dial'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="dialer-open-full-btn"
                onClick={() => { setOpen(false); navigate('/phone'); }}
                className="text-slate-400 hover:text-white transition-colors p-1"
                title="Open full Phone module"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid="dialer-close-btn"
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-white transition-colors p-1"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {isOnCall ? (
              /* Active Call */
              <div className="space-y-4">
                <div className="text-center space-y-1">
                  <div data-testid="dialer-caller-name" className="text-white font-semibold text-base truncate">
                    {callerInfo?.name || callerInfo?.number || 'Unknown'}
                  </div>
                  {callerInfo?.name && callerInfo?.name !== callerInfo?.number && (
                    <div className="text-slate-400 text-xs">{callerInfo?.number}</div>
                  )}
                  <span data-testid="call-status-badge" className={`
                    inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full
                    ${callStatus === 'in-progress' ? 'bg-emerald-500/20 text-emerald-400' : ''}
                    ${callStatus === 'ringing' ? 'bg-amber-500/20 text-amber-400' : ''}
                    ${callStatus === 'connecting' ? 'bg-blue-500/20 text-blue-400' : ''}
                  `}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      callStatus === 'in-progress' ? 'bg-emerald-400 animate-pulse' :
                      callStatus === 'ringing' ? 'bg-amber-400 animate-pulse' : 'bg-blue-400 animate-pulse'
                    }`} />
                    {callStatus === 'in-progress' && formatDuration(callDuration)}
                    {callStatus === 'ringing' && 'Ringing...'}
                    {callStatus === 'connecting' && 'Connecting...'}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-4">
                  <button
                    data-testid="dialer-mute-btn"
                    onClick={toggleMute}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all
                      ${isMuted ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40' : 'bg-white/[0.06] text-slate-400 hover:text-white hover:bg-white/10'}`}
                  >
                    {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                  <button
                    data-testid="dialer-hangup-btn"
                    onClick={hangUp}
                    className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-all shadow-lg"
                  >
                    <PhoneOff className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
            ) : (
              /* Quick dial */
              <div className="space-y-3">
                <input
                  data-testid="dialer-number-input"
                  type="tel"
                  value={phoneInput}
                  onChange={e => setPhoneInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="+1 (555) 000-0000"
                  className="
                    w-full bg-white/[0.05] border border-white/10 rounded-xl
                    px-3 py-2.5 text-white text-sm placeholder-slate-500
                    focus:outline-none focus:border-[#3399FF]/50 focus:bg-white/[0.08]
                    transition-all
                  "
                />
                <button
                  data-testid="dialer-call-btn"
                  onClick={handleCall}
                  disabled={!phoneInput.trim() || calling || !device}
                  className="
                    w-full flex items-center justify-center gap-2
                    bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:opacity-50
                    text-white text-sm font-medium py-2.5 rounded-xl transition-all
                  "
                >
                  <Phone className="w-4 h-4" />
                  {calling ? 'Calling...' : 'Call'}
                </button>

                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${isDeviceReady ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                  <span className="text-[10px] text-slate-500">
                    {isDeviceReady ? 'Ready' : device ? 'Initializing...' : 'Voice not configured'}
                  </span>
                </div>

                <button
                  onClick={() => { setOpen(false); navigate('/phone'); }}
                  className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-300 transition-colors w-full justify-center pt-1 border-t border-white/[0.05]"
                >
                  <ExternalLink className="w-3 h-3" />
                  Open full Phone
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
